import React, { useState } from 'react';
import { Database, AlertTriangle, RefreshCw, CheckCircle, Clock, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

/**
 * Factor governance dashboard (Screen 21).
 * @param {object} props
 * @param {object} props.governanceData - governance API response
 * @param {Function} [props.onBulkUpdate]
 * @param {Function} [props.onApproveCustomFactor]
 */
const FactorGovernance = ({ governanceData, onBulkUpdate, onApproveCustomFactor }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const data = governanceData;

  if (!data) {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <Database size={20} strokeWidth={1.5} />
          Factor Governance
        </h2>
        <Card className='bg-card'>
          <CardContent className='py-12 text-center text-muted-foreground'>
            Loading factor governance data...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <Database size={20} strokeWidth={1.5} />
          Factor Governance
        </h2>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold'>{data.summary?.totalFactorsInUse || 0}</p>
            <p className='text-xs text-muted-foreground'>Factors in Use</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-success'>{data.summary?.currentFactors || 0}</p>
            <p className='text-xs text-success'>Current</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-warning'>{data.summary?.expiredFactors || 0}</p>
            <p className='text-xs text-warning'>Expired</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-info'>{data.summary?.customFactors || 0}</p>
            <p className='text-xs text-info'>Custom</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-warning'>{data.summary?.pendingReview || 0}</p>
            <p className='text-xs text-warning'>Pending Review</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value='overview'>By Source</TabsTrigger>
          <TabsTrigger value='currency'>Currency Alerts</TabsTrigger>
          <TabsTrigger value='custom'>Custom Factors</TabsTrigger>
        </TabsList>

        <TabsContent value='overview'>
          <Card className='bg-card'>
            <CardContent className='p-0'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className='text-right'>Factors</TableHead>
                    <TableHead className='text-right'>Latest Year</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.factorsBySource || []).map((s) => (
                    <TableRow key={s.source}>
                      <TableCell className='font-medium text-sm'>{s.source}</TableCell>
                      <TableCell className='text-right text-sm'>{s.count}</TableCell>
                      <TableCell className='text-right text-sm'>{s.latestYear}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='currency'>
          {(data.currencyAlerts || []).length === 0 ? (
            <Card className='bg-card'>
              <CardContent className='py-8 text-center'>
                <CheckCircle size={32} className='mx-auto mb-2 text-success' strokeWidth={1.5} />
                <p className='text-muted-foreground'>All factors are current. No updates needed.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className='bg-card'>
              <CardContent className='p-0'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Current Factor</TableHead>
                      <TableHead>Newer Version</TableHead>
                      <TableHead className='text-right'>Affected Records</TableHead>
                      <TableHead className='text-right'>Emissions Impact</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.currencyAlerts.map((alert) => (
                      <TableRow key={alert.factorId}>
                        <TableCell>
                          <p className='text-sm font-medium'>{alert.name}</p>
                          <p className='text-xs text-muted-foreground'>Year: {alert.year}</p>
                        </TableCell>
                        <TableCell>
                          <p className='text-sm'>{alert.newerName}</p>
                          <Badge className='bg-success-bg text-success text-xs'>New</Badge>
                        </TableCell>
                        <TableCell className='text-right text-sm'>{alert.affectedRecords}</TableCell>
                        <TableCell className='text-right font-mono text-sm'>
                          {(alert.affectedEmissions || 0).toFixed(1)} tCO₂e
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => onBulkUpdate?.({
                              oldFactorId: alert.factorId,
                              newFactorId: alert.newerFactorId,
                            })}
                          >
                            <RefreshCw size={14} strokeWidth={1.5} />
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='custom'>
          {(data.customFactorQueue || []).length === 0 ? (
            <Card className='bg-card'>
              <CardContent className='py-8 text-center text-muted-foreground'>
                No custom factors pending review.
              </CardContent>
            </Card>
          ) : (
            <Card className='bg-card'>
              <CardContent className='p-0'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Factor Name</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className='text-right'>Used In</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.customFactorQueue.map((cf) => (
                      <TableRow key={cf.id}>
                        <TableCell className='font-medium text-sm'>{cf.name}</TableCell>
                        <TableCell className='text-sm'>{cf.createdBy}</TableCell>
                        <TableCell>
                          <Badge className='bg-warning-bg text-warning text-xs'>{cf.reviewStatus}</Badge>
                        </TableCell>
                        <TableCell className='text-right text-sm'>{cf.usageCount} records</TableCell>
                        <TableCell>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => onApproveCustomFactor?.(cf.id)}
                          >
                            <CheckCircle size={14} strokeWidth={1.5} />
                            Approve
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FactorGovernance;
